import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Analytics } from '@recruitee/analytics';
import { TranslatePipe } from '@recruitee/i18n';
import {
  OAuthAuthorizeActions,
  OAuthClientAuthActions,
  OAuthCountersActions,
} from '@recruitee/oauth-data';
import {
  IconComponent,
  IntegrationItemComponent,
  RtStatefulComponent,
  WindowRef,
} from '@recruitee/user-interface';

type State = {
  deauthorizeLoading: boolean;
};

@Component({
  selector: 'rt-zapier-integration',
  templateUrl: './zapier-integration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IntegrationItemComponent, IconComponent, TranslatePipe],
})
export class ZapierIntegrationComponent
  extends RtStatefulComponent<State>
  implements OnInit, OnDestroy
{
  @Input() public enabled: boolean = true;
  @Input() public loading: boolean = false;
  @Input() public active: boolean = false;
  @Input() public clientId: string | null = null;
  @Input() public teamIntegrationsCount: number = 0;
  @Output() public statusChange: EventEmitter<null> = new EventEmitter();

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private analytics: Analytics,
    private windowRef: WindowRef,
    private store: Store,
    private actions$: Actions,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.createState({
      deauthorizeLoading: false,
    });
  }

  public ngOnInit() {
    this.actions$
      .pipe(ofType(OAuthAuthorizeActions.deauthorizeSuccess), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({ deauthorizeLoading: false });
        this.reloadAuthorizedClients();
      });

    this.actions$
      .pipe(ofType(OAuthAuthorizeActions.deauthorizeFailed), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({ deauthorizeLoading: false });
        // eslint-disable-next-line ban/ban
        alert('We had some problems while trying to deauthorize Zapier. Please, try again.');
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public reloadAuthorizedClients(): void {
    this.store.dispatch(OAuthClientAuthActions.fetch());
    this.store.dispatch(OAuthCountersActions.fetch());
  }

  public authorize(): void {
    this.analytics.track('[Settings] IntegrationsZapierIntegrateButtonClick');
    this.windowRef.open('https://zapier.com/apps/recruitee/integrations', '_blank');
  }

  public deauthorize(): void {
    this.setState({ deauthorizeLoading: true });
    this.store.dispatch(
      OAuthAuthorizeActions.deauthorize({ payload: { clientId: this.clientId! } }),
    );
  }
}
